import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { TvFrameComponent } from '../../../preview-common/tv-frame/tv-frame.component';

/**
 * Layout for the Shorts ad on the TV app: the creative as tall as the screen
 * with the ad's copy in a companion column beside it rather than over it. The
 * column is a fixed panel at the screen's right edge, so the creative's right
 * edge lands in the same place whatever its shape and only the dark band to its
 * left changes.
 *
 * Presentational only, and not registered as a variant itself: the registered
 * per-format components wrap it and pass their own {@link format}.
 */
@Component({
    selector: 'app-gen-youtube-shorts-ad-tv-shell',
    imports: [CommonModule, TvFrameComponent],
    templateUrl: './youtube-shorts-ad-tv-shell.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-shorts-ad-tv-shell.component.scss'
})
export class YoutubeShortsAdTvShellComponent {
  @Input() data!: GenVideoPreviewData;

  /**
   * The creative format this placement previews, hardcoded by the wrapping
   * variant. The matching video is found by measured format rather than by
   * slot, so the author can paste the links in any order.
   */
  @Input() format: VideoFormat = '9:16';

  /** CSS aspect-ratio per format, bound onto the player box. */
  private readonly playerAspect: Record<VideoFormat, string> = {
    '9:16': '9 / 16',
    '1:1': '1 / 1',
    '16:9': '16 / 9',
  };

  /** The player's shape — its height is the screen's, so this sets its width. */
  get aspectRatio(): string {
    return this.playerAspect[this.format];
  }

  /** Thumbnail of the video in this format, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  /**
   * The column gives the headline two lines, so it takes the long headline and
   * only falls back to the short one when there isn't one.
   */
  get headline(): string {
    return this.data.longHeadlines[0] || this.data.headlines[0] || 'Your headline appears here';
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
